
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Event, EventFilters, EventType } from '@/lib/types';
import EventCard from './EventCard';
import FilterBar from './FilterBar';
import { allEvents, filterEvents } from '@/lib/events';
import EventListMarketingSection from './EventListMarketingSection';
import { useAuth } from '@/contexts/AuthContext';
import { markRecommendedEvents } from '@/utils/recommendationUtils';
import RecommendedEventBadge from './RecommendedEventBadge';

// Létrehozunk egy állandót a localStorage kulcsának
const FILTER_STORAGE_KEY = 'medihub-filter-settings';

// Segédfüggvény a mentett szűrők betöltéséhez EventFilters formátumban
const loadSavedFilters = (): EventFilters | undefined => {
  try {
    const savedFiltersString = localStorage.getItem(FILTER_STORAGE_KEY);
    if (!savedFiltersString) return undefined;
    
    const savedFilters = JSON.parse(savedFiltersString);
    
    // Átalakítjuk a mentett formátumot EventFilters formátumra és biztosítjuk a helyes típusokat
    const activeTypes = Object.entries(savedFilters.selectedTypes)
      .filter(([_, isSelected]) => isSelected)
      .map(([type]) => type as EventType);
      
    const activeProfessions = Object.entries(savedFilters.selectedProfessions)
      .filter(([_, isSelected]) => isSelected)
      .map(([profession]) => profession);
    
    return {
      search: savedFilters.search,
      location: savedFilters.location,
      type: activeTypes,
      professions: activeProfessions,
      hasCreditPoints: savedFilters.hasCreditPoints,
      dateRange: savedFilters.dateRange
    };
  } catch (error) {
    console.error('Hiba a mentett szűrők betöltésekor:', error);
    return undefined;
  }
};

interface EventListProps {
  title?: string;
  showFilters?: boolean;
  events?: Event[];
  limit?: number;
  variant?: 'grid' | 'list';
  cardVariant?: 'default' | 'small' | 'featured';
  showMarketingSection?: boolean;
  showRecommendedBadge?: boolean;
  loading?: boolean;
  onEventClick?: (event: Event) => void;
}

const EventList = ({
  title = 'Események',
  showFilters = true,
  events,
  limit,
  variant = 'grid',
  cardVariant = 'default',
  showMarketingSection = false,
  showRecommendedBadge = false,
  loading = false,
  onEventClick
}: EventListProps) => {
  const { user } = useAuth();
  const initialFilters = showFilters ? loadSavedFilters() || {} : {};
  const [filters, setFilters] = useState<EventFilters>(initialFilters);
  const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(loading);

  useEffect(() => {
    // Set loading state based on prop
    setIsLoading(loading);

    // Process events only if not loading
    if (!loading) {
      const timer = setTimeout(() => {
        const sourceEvents = events || allEvents;
        let filtered = showFilters ? filterEvents(sourceEvents, filters) : sourceEvents;

        // Mark recommended events based on user preferences
        filtered = markRecommendedEvents(filtered, user);

        setFilteredEvents(limit ? filtered.slice(0, limit) : filtered);
        setIsLoading(false);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [events, filters, limit, showFilters, user, loading]);

  const handleFilterChange = (newFilters: EventFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <div className="w-full font-sans">
      {title && (
        <h2 className="text-2xl font-bold mb-6">{title}</h2>
      )}

      {showFilters && (
        <div className="mb-6">
          <FilterBar onFilterChange={handleFilterChange} />
        </div>
      )}

      {showMarketingSection && (
        <EventListMarketingSection />
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="bg-gray-100 animate-pulse rounded-xl h-80"
            />
          ))}
        </div>
      ) : filteredEvents.length === 0 ? (
        <div className="text-center py-20">
          <h3 className="text-xl font-semibold text-gray-700">Nincs találat</h3>
          <p className="text-gray-500 mt-2">Próbáld meg módosítani a keresési feltételeket</p>
        </div>
      ) : (
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className={
            variant === 'grid'
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
              : 'space-y-6'
          }
        >
          {filteredEvents.map(event => (
            <motion.div key={event.id} variants={item} className="relative">
              {showRecommendedBadge && event.isRecommended && (
                <div className="absolute top-2 right-2 z-10">
                  <RecommendedEventBadge />
                </div>
              )}
              <EventCard
                event={event}
                variant={cardVariant}
                onClick={() => onEventClick && onEventClick(event)}
              />
            </motion.div>
          ))}
        </motion.div>
      )}
    </div>
  );
};

export default EventList;
